/**
 * Fait : le contrat PUBLIC des conversations — créer un fil, lancer une
 *   génération. Le formulaire du client et les routes de l'API valident avec
 *   les MÊMES schémas, bornes comprises : un texte de message est limité, et
 *   la limite est LA même que celle du modèle de données.
 *
 * Ne fait pas : il ne décrit pas les documents en base
 *   (`donnees/conversations.ts`) et ne porte aucun identifiant de modèle —
 *   la génération désigne une ENTRÉE DU CATALOGUE, jamais un nom de modèle
 *   en dur (critère 24).
 */

import { z } from 'zod';

import { identifiant, nomDonne } from './schemas.ts';

/**
 * Taille maximale du texte d'un message, en caractères. LA borne unique —
 * le modèle de données l'importe d'ici : très au-delà de tout message
 * familial réel, très en deçà de la limite BSON de 16 Mo.
 */
export const TAILLE_MAXIMALE_TEXTE_MESSAGE = 100_000;

/** Créer un fil de conversation dans un projet. */
export const demandeDeConversation = z.object({ titre: nomDonne }).strict();

export type DemandeDeConversation = z.infer<typeof demandeDeConversation>;

/**
 * Lancer une génération : la branche, le texte du membre, et l'entrée du
 * catalogue qui dit le modèle ET sa voie — le client ne nomme jamais un
 * modèle, il désigne une ligne du catalogue.
 */
export const demandeDeGeneration = z
  .object({
    brancheId: identifiant,
    entreeDeCatalogueId: identifiant,
    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_MESSAGE),
  })
  .strict();

export type DemandeDeGenerationHttp = z.infer<typeof demandeDeGeneration>;

/** Modifier un message — le sien — arbitré par version (D-011). */
export const demandeDeModificationDeMessage = z
  .object({
    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_MESSAGE),
    version: z.number().int().min(0),
  })
  .strict();

export type DemandeDeModificationDeMessage = z.infer<typeof demandeDeModificationDeMessage>;

/**
 * RÉGÉNÉRER en créant une branche : la nouvelle branche copie la
 * conversation jusqu'au message d'origine INCLUS, puis une génération part
 * dessus — l'ancienne lecture reste intacte, la nouvelle vit à côté.
 */
export const demandeDeBranche = z
  .object({
    messageDOrigineId: identifiant,
    entreeDeCatalogueId: identifiant,
  })
  .strict();

export type DemandeDeBranche = z.infer<typeof demandeDeBranche>;

/** Le plafond de la comparaison — le critère 29 refuse au-delà de TROIS. */
export const MODELES_MAXIMUM_EN_COMPARAISON = 3;

/**
 * Estimer avant de confirmer (critère 29) : les modèles pressentis, le
 * texte — la réponse donne le nombre d'appels et l'estimation HAUTE de
 * chacun. Plus de trois modèles : refusé à la validation.
 */
export const demandeDEstimation = z
  .object({
    brancheId: identifiant,
    entreesDeCatalogueIds: z.array(identifiant).min(1).max(MODELES_MAXIMUM_EN_COMPARAISON),
    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_MESSAGE),
  })
  .strict();

export type DemandeDEstimation = z.infer<typeof demandeDEstimation>;

/**
 * Comparer : DEUX ou trois modèles sur la même question — chaque modèle
 * reçoit sa propre branche, sa propre génération, sa propre réservation.
 */
export const demandeDeComparaison = z
  .object({
    brancheId: identifiant,
    entreesDeCatalogueIds: z.array(identifiant).min(2).max(MODELES_MAXIMUM_EN_COMPARAISON),
    texte: z.string().min(1).max(TAILLE_MAXIMALE_TEXTE_MESSAGE),
  })
  .strict();

export type DemandeDeComparaison = z.infer<typeof demandeDeComparaison>;
